import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Components
import { Button, Clickable, Col, Input, Row, Text } from 'components';
import {
  AccountLocked,
  AppleLogin,
  FbLogin,
  GgLogin,
  LoginAttempts
} from 'screens/Login/components';

// Utils
import { firebase, openMobileAppLink, storage } from 'core';
import { Api, theme, validateEmail } from 'lib';

const CallToActionLogin = ({
  setCurrentAuthStep,
  actionType,
  authSteps,
  otherUser,
  followAthlete,
  followUpDetails,
  handleLoadingSocialSignIn,
  navigateTo = null
}) => {
  const [email, setEmail] = useState('');
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const [emailErr, setEmailErr] = useState(null);
  const [password, setPassword] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [passErr, setPassErr] = useState(null);
  const [accountLockedVisible, setAccountLockedVisible] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const disabled = password.length < 8 || !validateEmail(email);
  const [isAPILoading, setIsAPILoading] = useState(false);

  function handleModal() {
    setAccountLockedVisible(!accountLockedVisible);
  }

  async function login() {
    // reset field errors from the previous attempt
    setEmailErr(null);
    setPassErr(null);

    // silent: 4xx bodies (pwErr, err, locked) come back for inline rendering
    const res = await Api.post('/users/login', { email, password }, 'login', { silent: true });
    if (!res) return;

    if (res.pwErr) {
      setPassErr(res.pwErr);
      setAttempts(res.loginAttempts);
      passwordInputRef.current.focus();
      return;
    }

    if (res.locked) {
      setAttempts(3);
      return handleModal();
    }

    if (res.err) {
      emailInputRef.current.focus();
      return setEmailErr(res.err);
    }

    if (res.migratedUser) {
      return navigate('/activate-account', { state: { email } });
    }

    // error without a field-level payload (network, 5xx) — show global toast
    if (!res.token) {
      return dispatch({
        type: 'HANDLE_RES',
        payload: { err: t('error.somethingWentWrong') + '\n' + t('error.pleaseTryAgainLater') }
      });
    }

    await storage.setItem('token', res.token);

    firebase.logEvent('web_login_from_cta_share', { method: 'email', type: actionType });

    if (followAthlete && typeof followAthlete === 'function') {
      setIsAPILoading(true);
      await followAthlete();
      setIsAPILoading(false);
    }

    const isShoutout = actionType === 'requestShoutout' || actionType === 'sendShoutout';

    dispatch({
      type: 'SET_USER',
      payload: {
        ...res.user,
        atheleteIdToSubscribe: actionType === 'subscribe' ? otherUser?._id : null,
        athleteUsernameToFollow: actionType === 'follow' ? otherUser?.username : null,
        atheleteUsernameToFace2Face: actionType === 'face2face' ? otherUser?.username : null,
        atheleteUsernameToAdPlayaz: actionType === 'adPlayaz' ? otherUser?.username : null,
        athleteUsernameToShoutout: isShoutout ? otherUser?.username : null,
        fromAtheleteUsername: actionType === 'fromUser' ? otherUser?.username : null,
        followUpAction: actionType,
        navigateTo
      }
    });
  }

  return (
    <>
      <Col centerAll wid="288px" marg="0 auto" pos="relative">
        <Input
          inputRef={emailInputRef}
          marg="32px 0 0"
          wid="100%"
          placeholder={t('common.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={emailErr}
        />

        <Input
          inputRef={passwordInputRef}
          marg="28px 0 0"
          wid="100%"
          password
          placeholder={t('common.password')}
          value={password}
          error={passErr}
          onChange={(e) => setPassword(e.target.value)}
        />

        <LoginAttempts attempts={attempts} />

        <Button
          marg="28px 0 0"
          wid="100%"
          text={t('common.login')}
          bg={disabled ? theme.GREY_20 : theme.SECONDARY}
          app
          onClick={!disabled ? login : null}
          action="login"
          loading={+isAPILoading}
        />

        <Clickable onClick={() => navigate('/forgot-password')}>
          <Text marg="24px 0 0" b1 bold>
            {t('auth.forgottenPassword')}
          </Text>
        </Clickable>

        <TextDivider>{t('common.or')}</TextDivider>

        <FbLogin
          handleLoadingSocialSignIn={handleLoadingSocialSignIn}
          redirectUrl={navigateTo}
          CustomButton={(props) => (
            <Button facebook onClick={props.onClick} text={t('auth.logIn')} />
          )}
        />

        <GgLogin
          handleLoadingSocialSignIn={handleLoadingSocialSignIn}
          followUpDetails={followUpDetails}
          redirectUrl={navigateTo}
          CustomButton={(props) => (
            <Button google onClick={props.onClick} marg="16px 0 0" text={t('auth.logIn')} />
          )}
        />

        <AppleLogin
          followUpDetails={followUpDetails}
          redirectUrl={navigateTo}
          CustomButton={(props) => (
            <Button apple onClick={props.onClick} marg="16px 0 0" text={t('auth.logIn')} />
          )}
        />

        <Text b1 marg="16px 0 0">
          {t('auth.getTheApp')}
        </Text>

        <Row wid="100%" gap="12px" marg="16px 0 0">
          <Clickable
            onClick={() => {
              openMobileAppLink(false);
            }}
          >
            <img src={require('assets/images/app-store.png')} alt="" />
          </Clickable>

          <Clickable
            onClick={() => {
              openMobileAppLink(true);
            }}
          >
            <img src={require('assets/images/google-play.png')} alt="" />
          </Clickable>
        </Row>

        <Clickable
          marg="20px 0 12px 0"
          onClick={() => {
            setCurrentAuthStep(authSteps.SIGN_UP_WITH_SOCIALS);
          }}
        >
          <Text b2>
            {t('auth.dontHaveAccount')}{' '}
            <Text spanned b2 med>
              {t('common.signUp')}
            </Text>
          </Text>
        </Clickable>
      </Col>
      <AccountLocked visible={accountLockedVisible} closeModal={handleModal} />
    </>
  );
};

const TextDivider = styled.p`
  position: relative;
  margin: 20px 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: lowercase;

  &::before {
    position: absolute;
    content: '';
    left: 0;
    top: 50%;
    background-color: #ccc;
    width: 40%;
    height: 1px;
  }
  &::after {
    position: absolute;
    content: '';
    right: 0;
    top: 50%;
    background-color: #ccc;
    width: 40%;
    height: 1px;
  }
`;

export default CallToActionLogin;
